import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { MatIconModule } from '@angular/material/icon';
import { SwUpdate, UnrecoverableStateEvent, VersionEvent } from '@angular/service-worker';
import { environment } from '../../../environments/environment';
import { CommonService } from '../../shared/services/common.service';
import { UserDataService } from '../../shared/services/user-data.service';
import { catchError, firstValueFrom, of } from 'rxjs';

interface Version {
  current_version: string;
  app_type: string;
}

@Component({
  selector: 'app-new-version-available',
  template: `
    <div class="version-container">
      <p-card>
        <div class="card-content">
          <h2>¡Nueva Versión Disponible!</h2>
          <div class="version-info">
            <p>Versión Actual: <strong>{{ currentVersion }}</strong></p>
            <p>Nueva Versión: <strong>{{ newVersion }}</strong></p>
          </div>
          <p>Una nueva versión de la aplicación está disponible. Por favor, actualice para continuar.</p>
          <div class="button-container">
            <p-button 
              label="Cerrar Sesión" 
              (onClick)="logout()" 
              severity="danger">
              <mat-icon fontSet="material-icons-outlined">logout</mat-icon>
            </p-button>
            <p-button 
              label="Actualizar Página" 
              (onClick)="updatePage()" 
              severity="primary">
              <mat-icon fontSet="material-icons-outlined">refresh</mat-icon>
            </p-button>
          </div>
          <div class="help-message">
            <small>Si la página no actualiza puede intentar apretando <strong>Ctrl + Shift + R</strong></small>
          </div>
        </div>
      </p-card>
    </div>
  `,
  styles: [`
    .version-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      min-height: 100vh;
      padding: 2rem;
      text-align: center;
      background-image: url('/assets/images/auth-background.jpg');
      background-size: cover;
      background-position: center;
      background-repeat: no-repeat;
    }
    .card-content {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 1rem;
    }
    h2 {
      font-size: 2rem;
      font-weight: 600;
      margin-bottom: 1rem;
    }
    .version-info {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      margin: 1rem 0;
    }
    .button-container {
      display: flex;
      gap: 1rem;
      margin-top: 1rem;
      width: 100%;
      justify-content: space-between;
    }
    :host ::ng-deep .p-card {
      background: rgba(255, 255, 255, 0.9);
      backdrop-filter: blur(10px);
      border-radius: 12px;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
      min-width: 320px;
    }
    :host ::ng-deep .p-button {
      min-width: 150px;
    }
    .material-symbols-outlined {
      margin-right: 0.5rem;
    }
    .help-message {
      margin-top: 1rem;
      padding: 0.5rem;
      color: #666;
      font-style: italic;
      text-align: center;
    }
  `],
  standalone: true,
  imports: [CommonModule, CardModule, ButtonModule, MatIconModule]
})
export class NewVersionAvailableComponent implements OnInit {
  currentVersion: string = environment.version;
  newVersion: string = '';

  constructor(
    private commonService: CommonService,
    private router: Router,
    private swUpdate: SwUpdate
  ) {}

  ngOnInit() {
    // Set up service worker update handlers
    if (this.swUpdate.isEnabled) {
      // Listen for available updates
      this.swUpdate.versionUpdates.subscribe((event: VersionEvent) => {
        if (event.type === 'VERSION_READY') {
          console.log('New version available:', event.latestVersion);
        }
      });
      
      // Listen for unrecoverable state events
      this.swUpdate.unrecoverable.subscribe((event: UnrecoverableStateEvent) => {
        console.error('Service worker entered an unrecoverable state:', event.reason);
        // Force a complete reset in this case
        this.fallbackUpdate();
      });
    }

    // Check current version from server
    this.commonService.getCurrentVersion()
      .pipe(
        catchError(error => {
          console.error('Error fetching current version:', error);
          return of([] as Version[]);
        })
      )
      .subscribe((versions: Version[]) => {
        if (versions && versions.length > 0) {
          this.newVersion = versions[0].current_version;
          // If versions match, redirect to layout
          if (this.newVersion === this.currentVersion) {
            this.router.navigate(['']);
          }
        }
      });
  }

  updatePage(): void {
    // Check if service worker is available and has updates
    if (this.swUpdate.isEnabled) {
      // Try to check for updates first
      this.swUpdate.checkForUpdate().then(updateFound => {
        if (updateFound) {
          console.log('Update found during check');
        }
        
        // Proceed with activation regardless
        return this.swUpdate.activateUpdate();
      }).then(() => {
        console.log('Service worker update activated');
        // Clear additional caches and reload
        this.clearAllCaches().then(() => {
          document.location.reload();
        });
      }).catch((error) => {
        console.warn('Service worker update failed:', error);
        // Fallback if service worker update fails
        this.fallbackUpdate();
      });
    } else {
      console.log('Service worker not enabled, using fallback update');
      // Fallback for development or when service worker is not available
      this.fallbackUpdate();
    }
  }

  private fallbackUpdate(): void {
    this.clearAllCaches().then(() => {
      // Try different cache-busting techniques
      try {
        // Method 1: Use Cache-Control header through fetch
        fetch(window.location.href, {
          method: 'GET',
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          }
        }).then(() => {
          // Method 2: Force a hard reload with cache bypass
          const url = window.location.href.split('?')[0];
          const cacheBuster = `?v=${Date.now()}`;
          window.location.href = url + cacheBuster;
        }).catch(() => {
          // Method 3: If fetch fails, just do a simple location.reload(true)
          try {
            (location as any).reload(true);
          } catch {
            location.reload();
          }
        });
      } catch (e) {
        // Final fallback
        console.warn('All sophisticated update methods failed, using simple reload');
        location.reload();
      }
    });
  }

  private async clearAllCaches(): Promise<void> {
    console.log('Starting cache clearing process...');
    const promises: Promise<any>[] = [];
    
    // Clear Cache Storage API caches
    if ('caches' in window) {
      try {
        console.log('Clearing Cache Storage API...');
        const cacheNames = await caches.keys();
        console.log(`Found ${cacheNames.length} caches to clear`);
        promises.push(...cacheNames.map(name => {
          console.log(`Deleting cache: ${name}`);
          return caches.delete(name);
        }));
      } catch (e) {
        console.warn('Error clearing Cache Storage API:', e);
      }
    }
    
    // Clear localStorage and sessionStorage
    try {
      console.log('Clearing localStorage and sessionStorage...');
      localStorage.clear();
      sessionStorage.clear();
    } catch (e) {
      console.warn('Could not clear storage:', e);
    }
    
    // Clear IndexedDB (if used)
    if ('indexedDB' in window) {
      try {
        console.log('Clearing IndexedDB databases...');
        const databases = await indexedDB.databases();
        console.log(`Found ${databases.length} IndexedDB databases to clear`);
        
        promises.push(...databases.map(db => {
          if (db.name) {
            console.log(`Deleting IndexedDB database: ${db.name}`);
            return new Promise<void>((resolve) => {
              const deleteReq = indexedDB.deleteDatabase(db.name!);
              deleteReq.onsuccess = () => {
                console.log(`Successfully deleted IndexedDB: ${db.name}`);
                resolve();
              };
              deleteReq.onerror = (event) => {
                console.warn(`Error deleting IndexedDB ${db.name}:`, event);
                resolve(); // Don't fail if can't delete
              };
            });
          }
          return Promise.resolve();
        }));
      } catch (e) {
        console.warn('Could not clear IndexedDB:', e);
      }
    }
    
    // Unregister service workers
    if ('serviceWorker' in navigator) {
      try {
        console.log('Unregistering service workers...');
        const registrations = await navigator.serviceWorker.getRegistrations();
        console.log(`Found ${registrations.length} service workers to unregister`);
        
        promises.push(...registrations.map(async registration => {
          console.log(`Unregistering service worker: ${registration.scope}`);
          const success = await registration.unregister();
          console.log(`Service worker unregistration ${success ? 'successful' : 'failed'}`);
          return success;
        }));
      } catch (e) {
        console.warn('Error unregistering service workers:', e);
      }
    }
    
    // Note: Application cache is deprecated and removed from modern browsers
    // We're keeping this conditional check for older browsers but with proper TypeScript handling
    try {
      // Using optional chaining to safely check/access applicationCache if it exists
      if ((window as any).applicationCache) {
        console.log('Clearing application cache...');
        (window as any).applicationCache.swapCache();
      }
    } catch (e) {
      console.warn('Error clearing application cache:', e);
    }
    
    // Wait for all promises to complete
    try {
      await Promise.all(promises);
      console.log('All cache clearing operations completed');
    } catch (e) {
      console.error('Error during cache clearing:', e);
    }
  }

  logout(): void {
    this.router.navigate(['/logout']);
  }
}
